import { useState } from "react";
import { Reveal } from "./Reveal";

// ---------------------------------------------------------------------------
// AUDIENCE — "Hecho para quien vive de pasar presupuestos."
// Two big blocks: Servicios vs Distribuidoras/Mayoristas.
// ---------------------------------------------------------------------------

type Audience = {
  id: "servicios" | "distribuidores";
  title: string;
  pain: string;
  tags: string[];
};

const audiences: Record<"servicios" | "distribuidores", Audience> = {
  servicios: {
    id: "servicios",
    title: "Empresas de servicios",
    pain: "Pasás presupuestos y esperás respuesta. El que contesta primero se lleva el trabajo, y a tus clientes de hace un año nadie les volvió a escribir.",
    tags: [
      "Aberturas",
      "Piletas",
      "Climatización",
      "Reformas",
      "Plomería",
      "Electricidad",
      "Talleres mecánicos",
      "Pintura",
    ],
  },
  distribuidores: {
    id: "distribuidores",
    title: "Distribuidoras, importadoras y mayoristas",
    pain: "Te piden lista, stock y envío. Tenés una base de revendedores dormida y consultas que entran a cualquier hora que se contestan cuando se puede.",
    tags: [
      "Corralones",
      "Alimentos",
      "Bazar y blanquería",
      "Repuestos",
      "Tecnología",
      "Fábricas",
      "Bebidas",
      "Inmobiliaria",
    ],
  },
};

const fitYes = [
  "Ya tenés el negocio funcionando, con clientes y facturación",
  "Tenés capacidad para crecer",
  "Recibís consultas y enviás presupuestos",
  "Tenés clientes anteriores a los que nunca volviste a escribir",
  "Querés ordenar el seguimiento y dejar de perder oportunidades",
];

const fitNo = [
  "Recién estás arrancando y todavía no tenés clientes",
  "Vendés de mostrador a precio fijo, sin pasar presupuestos ni pedidos",
  "Buscás que te manejemos la publicidad (eso se cotiza aparte, primero va la base)",
  "Buscás lo más barato del mercado",
];

export function HomeAudience() {
  const [tab, setTab] = useState<"servicios" | "distribuidores">("servicios");
  const cur = audiences[tab];

  return (
    <section
      data-screen-label="Para quién"
      id="para-quien-es"
      className="mx-auto px-6 scroll-mt-16"
      style={{
        maxWidth: 1240,
        paddingTop: "clamp(80px,10vw,128px)",
        paddingBottom: "clamp(80px,10vw,128px)",
      }}
    >
      <Reveal>
        <div
          className="font-mono text-[13px] text-[#A78BFA]"
          style={{ letterSpacing: "0.08em" }}
        >
          07 — PARA QUIÉN
        </div>
        <div className="flex justify-between items-end gap-6 flex-wrap mt-5">
          <h2
            className="text-[#EEEDF2] font-medium"
            style={{
              fontSize: "clamp(36px,4.8vw,64px)",
              lineHeight: 1.02,
              letterSpacing: "-0.025em",
              maxWidth: 720,
              textWrap: "balance" as never,
            }}
          >
            Hecho para quien vive de pasar presupuestos.
          </h2>
          <div className="flex gap-1 p-1 border border-[#2A2633] rounded-full">
            {(["servicios", "distribuidores"] as const).map((r) => {
              const active = r === tab;
              return (
                <button
                  key={r}
                  onClick={() => setTab(r)}
                  className="text-[14px] font-medium rounded-full px-4 py-2 transition-colors"
                  style={{
                    background: active ? "#7C3AED" : "transparent",
                    color: active ? "#fff" : "#9C98A6",
                  }}
                >
                  {r === "servicios" ? "Servicios" : "Distribuidores"}
                </button>
              );
            })}
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div
          className="grid gap-6 mt-10"
          style={{
            gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,360px),1fr))",
          }}
        >
          <div className="bg-[#13111A] border border-[#221F2A] rounded-2xl p-8 flex flex-col gap-[18px]">
            <h3 className="text-[22px] font-semibold tracking-[-0.01em] text-[#EEEDF2]">
              {cur.title}
            </h3>
            <p className="text-[#BAB6C4] leading-[1.6]">{cur.pain}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {cur.tags.map((t) => (
                <span
                  key={t}
                  className="text-[13px] rounded-full px-3 py-1.5 border border-[#2E2A38] text-[#BAB6C4]"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>

          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))",
            }}
          >
            <div className="border border-[#221F2A] rounded-2xl p-6">
              <div
                className="font-mono text-[12px] text-[#A78BFA]"
                style={{ letterSpacing: "0.06em" }}
              >
                ES PARA VOS SI
              </div>
              <div className="flex flex-col gap-3 mt-4">
                {fitYes.map((x) => (
                  <div
                    key={x}
                    className="grid leading-[1.45] text-[15px] text-[#EEEDF2]"
                    style={{ gridTemplateColumns: "20px 1fr", gap: 8 }}
                  >
                    <span className="text-[#A78BFA]">✓</span>
                    <span>{x}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="border border-[#221F2A] rounded-2xl p-6">
              <div
                className="font-mono text-[12px] text-[#E89A7A]"
                style={{ letterSpacing: "0.06em" }}
              >
                NO ES PARA VOS SI
              </div>
              <div className="flex flex-col gap-3 mt-4">
                {fitNo.map((x) => (
                  <div
                    key={x}
                    className="grid leading-[1.45] text-[15px] text-[#BAB6C4]"
                    style={{ gridTemplateColumns: "20px 1fr", gap: 8 }}
                  >
                    <span className="text-[#E89A7A]">—</span>
                    <span>{x}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
